import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatUSD } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Truck, Search, Eye, Trash2, Loader2, RefreshCw, Package, DollarSign, EyeOff, Plus, X, Save, Mail, MessageCircle, CheckCircle2 } from "lucide-react";
import { CreatePODialog } from "@/components/admin/StandSupplierOrdersSection";

const inputCls = "bg-black border-zinc-700 rounded-sm focus:border-[#FF5A00] focus:ring-0 text-sm";

const STATUS_LABEL = {
  BORRADOR: "Borrador",
  LISTO_PARA_ENVIAR: "Listo para enviar",
  ENVIADO_EMAIL: "Enviado por email",
  ENVIADO_WHATSAPP: "Enviado por WhatsApp",
  RECIBIDO: "Recibido",
  VISTO: "Visto",
  PENDIENTE_RESPUESTA: "Pendiente respuesta",
  ACEPTADO: "Aceptado",
  ACEPTADO_CON_OBSERVACIONES: "Aceptado c/ observaciones",
  RECHAZADO: "Rechazado",
  MODIFICACION_SOLICITADA: "Modificación solicitada",
  EN_PREPARACION: "En preparación",
  LISTO_PARA_ENTREGA: "Listo para entrega",
  ENTREGADO: "Entregado",
  FINALIZADO: "Finalizado",
  CANCELADO: "Cancelado",
};

const STATUS_TONE = {
  BORRADOR: "border-zinc-700 text-zinc-300",
  LISTO_PARA_ENVIAR: "border-amber-500/40 text-amber-300",
  ENVIADO_EMAIL: "border-blue-500/40 text-blue-300",
  ENVIADO_WHATSAPP: "border-emerald-500/40 text-emerald-300",
  ACEPTADO: "border-emerald-500/40 text-emerald-300",
  RECHAZADO: "border-red-500/40 text-red-300",
  CANCELADO: "border-zinc-800 text-zinc-500",
  FINALIZADO: "border-emerald-500/40 text-emerald-200",
};

export default function SupplierOrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "", supplier: "" });
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/supplier-orders");
      setOrders(data || []);
    } catch (e) { toast.error("No se pudo cargar el listado"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return orders.filter((o) => {
      if (filters.status && o.status !== filters.status) return false;
      if (filters.supplier && !((o.supplier_name || "").toLowerCase().includes(filters.supplier.toLowerCase()))) return false;
      if (q) {
        const hay = `${o.po_number} ${o.supplier_name} ${o.cliente_empresa} ${o.stand_number}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [orders, filters]);

  const supplierOptions = useMemo(() => {
    const set = new Set(orders.map((o) => o.supplier_name).filter(Boolean));
    return Array.from(set).sort();
  }, [orders]);

  return (
    <div className="space-y-4" data-testid="supplier-orders-panel">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-base font-semibold flex items-center gap-2"><Truck className="w-4 h-4 text-[#FF5A00]" /> Pedidos a proveedores</div>
          <div className="text-xs text-zinc-500 mt-0.5">{orders.length} pedido{orders.length !== 1 ? "s" : ""} · desde ítems, grupos o proyectos completos</div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="supplier-orders-reload" onClick={load} variant="outline" size="sm" className="border-zinc-700 hover:bg-zinc-900 rounded-sm">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
          </Button>
          <Button data-testid="new-po-general-btn" onClick={() => setShowNew(true)} size="sm" className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm">
            <Plus className="w-4 h-4 mr-2" /> Nuevo pedido
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
        <CardContent className="p-4 grid md:grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Buscar</Label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-zinc-500" />
              <Input data-testid="filter-q" className={`${inputCls} pl-8`} placeholder="PO-XXX, empresa, stand…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Estado</Label>
            <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-status" className={inputCls}><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 rounded-sm max-h-72">
                <SelectItem value="all">Todos los estados</SelectItem>
                {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Proveedor</Label>
            <Select value={filters.supplier || "all"} onValueChange={(v) => setFilters({ ...filters, supplier: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-supplier" className={inputCls}><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 rounded-sm max-h-72">
                <SelectItem value="all">Todos los proveedores</SelectItem>
                {supplierOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Listado */}
      {loading ? (
        <div className="flex items-center gap-2 text-zinc-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardContent className="p-8 text-center text-sm text-zinc-500">
            {orders.length === 0 ? "Todavía no creaste ningún pedido a proveedor." : "Sin resultados con los filtros actuales."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((o) => (
            <Card key={o.id} data-testid={`po-row-${o.po_number}`} className="bg-zinc-900 border-zinc-800 rounded-sm hover:border-zinc-700 transition-colors">
              <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-white">{o.po_number}</span>
                    <Badge variant="outline" className={`rounded-sm text-[10px] ${STATUS_TONE[o.status] || "border-zinc-700 text-zinc-400"}`}>{STATUS_LABEL[o.status] || o.status}</Badge>
                    {o.version > 1 && <Badge variant="outline" className="rounded-sm text-[10px] border-zinc-700 text-zinc-400">v{o.version}</Badge>}
                    <Badge variant="outline" className="rounded-sm text-[10px] border-zinc-700 text-zinc-400">Stand {o.stand_number}</Badge>
                  </div>
                  <div className="text-xs text-zinc-400 mt-1">
                    <span className="text-zinc-300">{o.supplier_name || "Sin proveedor asignado"}</span>
                    {o.cliente_empresa && <span> · Cliente {o.cliente_empresa}</span>}
                    <span className="text-zinc-500"> · {(o.items || []).length} ítem{(o.items || []).length !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Total {o.moneda}</div>
                    <div className="font-mono text-sm text-white">{o.moneda === "USD" ? formatUSD(o.totals?.total_final || 0) : `${(o.totals?.total_final || 0).toLocaleString()} ARS`}</div>
                  </div>
                  <Button data-testid={`po-detail-${o.po_number}`} onClick={() => setSelected(o.id)} size="sm" variant="outline" className="border-zinc-700 hover:bg-zinc-800 rounded-sm"><Eye className="w-4 h-4 mr-1.5" /> Ver</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {selected && (
        <SupplierOrderDialog poId={selected} onClose={() => { setSelected(null); load(); }} />
      )}

      {showNew && (
        <NewPOFromPanel onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />
      )}
    </div>
  );
}

// ----- Modal "Nuevo pedido" con selector de stand -----
function NewPOFromPanel({ onClose, onCreated }) {
  const [stands, setStands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stands");
        setStands(data || []);
      } catch { toast.error("No pude cargar los stands"); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return stands.slice(0, 60);
    return stands.filter((x) => {
      const hay = `${x.stand_number} ${x.code} ${x.company || ""} ${(x.configuration?.datos_facturacion?.empresa) || ""}`.toLowerCase();
      return hay.includes(s);
    }).slice(0, 60);
  }, [stands, q]);

  if (picked) {
    return (
      <Dialog open onOpenChange={() => onClose()}>
        <CreatePODialog stand={picked} onCreated={onCreated} />
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent data-testid="new-po-stand-picker" className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xl">
        <DialogHeader><DialogTitle>Nuevo pedido a proveedor · Elegí el stand</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-zinc-500" />
            <Input data-testid="new-po-stand-search" autoFocus className={`${inputCls} pl-8`} placeholder="Buscar por número, código o empresa…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {loading ? (
            <div className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Cargando stands…</div>
          ) : filtered.length === 0 ? (
            <div className="text-xs text-zinc-500 text-center py-6">Sin resultados</div>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto space-y-1">
              {filtered.map((s) => (
                <button key={s.id} type="button" data-testid={`new-po-pick-${s.stand_number}`} onClick={() => setPicked(s)}
                  className="w-full text-left p-2 border border-zinc-800 rounded-sm hover:border-[#FF5A00] hover:bg-[#FF5A00]/5 transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-mono text-sm text-white">{s.stand_number} <span className="text-zinc-500 text-xs">· {s.code}</span></div>
                      <div className="text-xs text-zinc-400 truncate">{s.company || s.configuration?.datos_facturacion?.empresa || "Sin asignar"}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant="outline" className={`rounded-sm text-[10px] ${s.full_equipado ? "border-[#FF5A00]/40 text-[#FF5A00]" : "border-teal-500/40 text-teal-300"}`}>
                        {s.full_equipado ? "FULL EQUIPADO" : "SOLO ESPACIO"}
                      </Badge>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ----- Modal detalle -----
function SupplierOrderDialog({ poId, onClose }) {
  const [po, setPo] = useState(null);
  const [internal, setInternal] = useState(null);
  const [supplierView, setSupplierView] = useState(null);
  const [tab, setTab] = useState("detail");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [ajusteManual, setAjusteManual] = useState(0);
  const [ajusteMotivo, setAjusteMotivo] = useState("");
  const [editingItems, setEditingItems] = useState(null); // null = no está editando

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/supplier-orders/${poId}`);
    setPo(data);
    setStatus(data.status);
    setAjusteManual(data.ajuste_manual || 0);
    setAjusteMotivo(data.ajuste_motivo || "");
    const [i, sv] = await Promise.all([
      api.get(`/admin/supplier-orders/${poId}/internal-summary`).then((r) => r.data).catch(() => null),
      api.get(`/admin/supplier-orders/${poId}/supplier-view`).then((r) => r.data).catch(() => null),
    ]);
    setInternal(i); setSupplierView(sv);
  }, [poId]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.put(`/admin/supplier-orders/${poId}`, patch);
      toast.success("Pedido actualizado");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const startEditItems = () => {
    setEditingItems((po.items || []).map((it) => ({
      id: it.id, source: it.source || "custom", source_ref_id: it.source_ref_id,
      name: it.name || "", unit: it.unit || "u",
      cantidad: it.cantidad || 0, costo_unit: it.costo_unit || 0,
      precio_venta_cliente_unit: it.precio_venta_cliente_unit ?? "",
      iva_rate: it.iva_rate || 21,
    })));
  };
  const cancelEditItems = () => setEditingItems(null);
  const updateEditItem = (id, field, val) => setEditingItems((ls) => ls.map((l) => l.id === id ? { ...l, [field]: val } : l));
  const removeEditItem = (id) => setEditingItems((ls) => ls.filter((l) => l.id !== id));
  const addEditItem = () => setEditingItems((ls) => [...ls, {
    id: `poi_new_${Date.now()}`, source: "custom", name: "", unit: "u", cantidad: 1, costo_unit: 0, precio_venta_cliente_unit: "", iva_rate: 21,
  }]);
  const saveEditItems = async () => {
    const clean = editingItems.filter((l) => l.name.trim() && Number(l.cantidad) > 0).map((l) => ({
      id: l.id.startsWith("poi_new_") ? undefined : l.id,
      source: l.source, source_ref_id: l.source_ref_id,
      name: l.name.trim(), unit: l.unit || "u",
      cantidad: Number(l.cantidad || 0), costo_unit: Number(l.costo_unit || 0),
      precio_venta_cliente_unit: l.precio_venta_cliente_unit === "" || l.precio_venta_cliente_unit == null ? null : Number(l.precio_venta_cliente_unit),
      iva_rate: Number(l.iva_rate || 21),
    }));
    if (clean.length === 0) return toast.error("El pedido debe tener al menos una línea con nombre y cantidad");
    await save({ items: clean });
    setEditingItems(null);
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar este pedido? Solo funciona en estado BORRADOR.")) return;
    try {
      await api.delete(`/admin/supplier-orders/${poId}`);
      toast.success("Pedido eliminado");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar"); }
  };

  const sendEmail = async () => {
    if (!po?.supplier_email) return toast.error("El pedido no tiene email del proveedor cargado");
    if (!window.confirm(`Enviar el pedido a ${po.supplier_email}?\n\nBCC interno: info@ y celeste@ serviciosoficiales.com.ar`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/supplier-orders/${poId}/send-email`);
      toast.success(data.sent_email_mocked ? "Correo simulado (SMTP no configurado)" : `Correo enviado a ${data.sent_email_to}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo enviar el correo"); }
    finally { setSaving(false); }
  };

  const openWhatsapp = async () => {
    try {
      const { data } = await api.get(`/admin/supplier-orders/${poId}/whatsapp-link`);
      if (!data.phone_digits) {
        toast.warning("El pedido no tiene número de WhatsApp. Se abrirá WhatsApp para elegir contacto.");
      }
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo generar el link"); }
  };

  const copyWhatsappMessage = async () => {
    try {
      const { data } = await api.get(`/admin/supplier-orders/${poId}/whatsapp-link`);
      await navigator.clipboard.writeText(data.message);
      toast.success("Mensaje copiado al portapapeles");
    } catch (e) { toast.error("No se pudo copiar el mensaje"); }
  };

  const markWhatsappSent = async () => {
    if (!window.confirm("¿Marcar como enviado por WhatsApp? El estado del pedido cambiará a ENVIADO_WHATSAPP.")) return;
    setSaving(true);
    try {
      await api.post(`/admin/supplier-orders/${poId}/mark-whatsapp-sent`);
      toast.success("Pedido marcado como enviado por WhatsApp");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo marcar"); }
    finally { setSaving(false); }
  };

  if (!po) return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-4xl">
        <DialogHeader><DialogTitle>Cargando…</DialogTitle></DialogHeader>
      </DialogContent>
    </Dialog>
  );

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="font-mono">{po.po_number}</span>
            <Badge variant="outline" className={`rounded-sm text-[10px] ${STATUS_TONE[po.status] || "border-zinc-700 text-zinc-400"}`}>{STATUS_LABEL[po.status] || po.status}</Badge>
            {po.version > 1 && <Badge variant="outline" className="rounded-sm text-[10px] border-zinc-700 text-zinc-400">v{po.version}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {/* Tabs de vista */}
        <div className="flex items-center gap-2 border-b border-zinc-800 mb-3">
          {[["detail", "Detalle"], ["internal", "Vista interna · margen"], ["supplier", "Vista proveedor"]].map(([k, l]) => (
            <button key={k} data-testid={`podetail-tab-${k}`} onClick={() => setTab(k)}
              className={`px-3 py-2 text-xs uppercase tracking-wide ${tab === k ? "text-white border-b-2 border-[#FF5A00]" : "text-zinc-500 hover:text-zinc-300"}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "detail" && (
          <div className="space-y-4 text-sm">
            <div className="grid md:grid-cols-2 gap-3">
              <MetaBlock title="Proveedor" rows={[["Nombre", po.supplier_name], ["Email", po.supplier_email], ["WhatsApp", po.supplier_whatsapp]]} />
              <MetaBlock title="Cliente" rows={[["Empresa", po.cliente_empresa], ["Contacto", po.cliente_contacto], ["Stand", po.stand_number]]} />
              <MetaBlock title="Condiciones de pago" rows={[
                ["Moneda", po.moneda], ["Anticipo %", `${po.anticipo_pct || 0}%`], ["Anticipo", po.totals?.anticipo_monto?.toLocaleString?.() || 0],
                ["Saldo", po.totals?.saldo_monto?.toLocaleString?.() || 0], ["Vencimiento", po.fecha_vencimiento || "—"], ["Tipo de cambio", po.tipo_cambio || "—"],
                ["Condiciones", po.condiciones_pago || "—"],
              ]} />
              <MetaBlock title="Compartir con proveedor" rows={[
                ["Contacto", po.compartir?.nombre ? "sí" : "no"], ["Email", po.compartir?.email ? "sí" : "no"],
                ["WhatsApp", po.compartir?.whatsapp ? "sí" : "no"], ["Facturación", po.compartir?.facturacion ? "sí" : "no"],
              ]} />
            </div>
            <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold flex items-center gap-2"><Package className="w-3.5 h-3.5" /> Ítems ({(po.items || []).length})</div>
                  {editingItems === null ? (
                    <Button data-testid="po-edit-items-btn" size="sm" variant="outline" onClick={startEditItems} className="border-zinc-700 hover:bg-zinc-800 rounded-sm h-7 text-xs">
                      Editar ítems
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button data-testid="po-add-edit-line" size="sm" variant="outline" onClick={addEditItem} className="border-zinc-700 hover:bg-zinc-800 rounded-sm h-7 text-xs">
                        <Plus className="w-3 h-3 mr-1" /> Agregar línea
                      </Button>
                      <Button data-testid="po-cancel-items" size="sm" variant="ghost" onClick={cancelEditItems} className="text-zinc-400 hover:text-white h-7 text-xs">
                        Cancelar
                      </Button>
                      <Button data-testid="po-save-items" size="sm" onClick={saveEditItems} disabled={saving} className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm h-7 text-xs">
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 mr-1" /> Guardar ítems</>}
                      </Button>
                    </div>
                  )}
                </div>
                {editingItems === null ? (
                  <div className="space-y-2">
                    {(po.items || []).map((it) => (
                      <div key={it.id} className="border border-zinc-800 rounded-sm p-3 bg-zinc-950 text-xs">
                        <div className="flex items-baseline justify-between gap-3">
                          <div>
                            <div className="text-white">{it.name} <span className="text-zinc-500">× {it.cantidad}{it.unit}</span></div>
                            {it.description && <div className="text-zinc-500 mt-0.5">{it.description}</div>}
                          </div>
                          <div className="text-right font-mono">
                            <div>Costo unit: {it.costo_unit?.toLocaleString?.() || 0}</div>
                            {it.precio_venta_cliente_unit != null && <div className="text-emerald-300">Cliente: {it.precio_venta_cliente_unit.toLocaleString()}</div>}
                          </div>
                        </div>
                      </div>
                    ))}
                    {(po.items || []).length === 0 && <div className="text-xs text-zinc-500 text-center py-3">Sin ítems.</div>}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {editingItems.map((l, idx) => (
                      <div key={l.id} className="border border-zinc-800 rounded-sm p-2 bg-zinc-950" data-testid={`po-edit-row-${idx}`}>
                        <div className="grid md:grid-cols-12 gap-2 items-end">
                          <div className="md:col-span-4">
                            <Label className="text-[10px] text-zinc-500">Nombre</Label>
                            <Input data-testid={`po-edit-name-${idx}`} className={inputCls} value={l.name} onChange={(e) => updateEditItem(l.id, "name", e.target.value)} />
                          </div>
                          <div className="md:col-span-1">
                            <Label className="text-[10px] text-zinc-500">Unidad</Label>
                            <Input className={inputCls} value={l.unit} onChange={(e) => updateEditItem(l.id, "unit", e.target.value)} />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-[10px] text-zinc-500">Cantidad</Label>
                            <Input type="number" data-testid={`po-edit-qty-${idx}`} className={inputCls} value={l.cantidad} onChange={(e) => updateEditItem(l.id, "cantidad", e.target.value)} />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-[10px] text-zinc-500">Costo unit</Label>
                            <Input type="number" data-testid={`po-edit-cost-${idx}`} className={inputCls} value={l.costo_unit} onChange={(e) => updateEditItem(l.id, "costo_unit", e.target.value)} />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-[10px] text-zinc-500">Precio cliente</Label>
                            <Input type="number" className={inputCls} value={l.precio_venta_cliente_unit} onChange={(e) => updateEditItem(l.id, "precio_venta_cliente_unit", e.target.value)} placeholder="Opcional" />
                          </div>
                          <div className="md:col-span-1 flex justify-end">
                            <Button type="button" size="sm" variant="ghost" data-testid={`po-edit-remove-${idx}`} onClick={() => removeEditItem(l.id)} className="text-zinc-500 hover:text-red-400 h-8 px-2">
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {editingItems.length === 0 && (
                      <div className="text-xs text-amber-300 text-center py-3 border border-amber-500/30 bg-amber-500/5 rounded-sm">
                        Agregá al menos una línea antes de guardar.
                      </div>
                    )}
                  </div>
                )}
                <div className="mt-3 border-t border-zinc-800 pt-3 text-xs grid grid-cols-2 gap-2">
                  <PriceRow k="Subtotal neto" v={po.totals?.subtotal_neto} moneda={po.moneda} />
                  <PriceRow k="IVA" v={po.totals?.iva_total} moneda={po.moneda} />
                  {po.ajuste_motivo && <PriceRow k={`Ajuste (${po.ajuste_motivo})`} v={po.totals?.ajuste_manual} moneda={po.moneda} />}
                  <PriceRow k="Total final" v={po.totals?.total_final} moneda={po.moneda} emphasize />
                </div>
              </CardContent>
            </Card>

            {/* Envío al proveedor */}
            <Card data-testid="po-envio-block" className="bg-zinc-900 border-zinc-800 rounded-sm">
              <CardContent className="p-4 space-y-3">
                <div className="text-xs font-semibold flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#FF5A00]" /> Envío al proveedor</div>
                <div className="text-[11px] text-zinc-500">Al enviar por email, se copia (BCC oculto) a <span className="text-zinc-400">info@</span> y <span className="text-zinc-400">celeste@</span> serviciosoficiales.com.ar como archivo interno.</div>
                <div className="flex flex-wrap gap-2">
                  <Button data-testid="po-send-email-btn" onClick={sendEmail} disabled={saving || !po.supplier_email} className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm">
                    <Mail className="w-4 h-4 mr-1.5" /> Enviar por email
                  </Button>
                  <Button data-testid="po-open-whatsapp-btn" onClick={openWhatsapp} variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 rounded-sm">
                    <MessageCircle className="w-4 h-4 mr-1.5" /> Abrir WhatsApp
                  </Button>
                  <Button data-testid="po-copy-whatsapp-btn" onClick={copyWhatsappMessage} variant="outline" className="border-zinc-700 hover:bg-zinc-800 rounded-sm">
                    Copiar mensaje
                  </Button>
                  <Button data-testid="po-mark-wa-sent-btn" onClick={markWhatsappSent} disabled={saving} variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Marcar WhatsApp enviado
                  </Button>
                </div>
                {(po.sent_email_at || po.sent_whatsapp_at) && (
                  <div className="text-[11px] text-zinc-500 space-y-0.5 pt-2 border-t border-zinc-800">
                    {po.sent_email_at && (
                      <div>
                        Último email a <span className="text-zinc-300 font-mono">{po.sent_email_to}</span> · {po.sent_email_at.slice(0,16).replace("T"," ")} UTC{po.sent_email_mocked ? " · simulado (SMTP no configurado)" : ""}
                      </div>
                    )}
                    {po.sent_whatsapp_at && (
                      <div>
                        Último WhatsApp a <span className="text-zinc-300 font-mono">{po.sent_whatsapp_to || "(sin número)"}</span> · {po.sent_whatsapp_at.slice(0,16).replace("T"," ")} UTC
                      </div>
                    )}
                  </div>
                )}
                {!po.supplier_email && (
                  <div className="text-[11px] text-amber-300 border border-amber-500/30 bg-amber-500/5 rounded-sm p-2">
                    Este pedido no tiene email del proveedor cargado. Editá los datos para habilitar el envío por email.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Acciones */}
            <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
              <CardContent className="p-4 space-y-3">
                <div className="text-xs font-semibold">Acciones</div>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Cambiar estado</Label>
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger className={`${inputCls} w-56`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700 rounded-sm max-h-72">
                        {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Ajuste manual</Label>
                    <Input type="number" className={`${inputCls} w-32`} value={ajusteManual} onChange={(e) => setAjusteManual(Number(e.target.value))} />
                  </div>
                  <div className="flex-1 min-w-[240px]">
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Motivo del ajuste</Label>
                    <Input className={inputCls} value={ajusteMotivo} onChange={(e) => setAjusteMotivo(e.target.value)} />
                  </div>
                  <Button data-testid="po-save" onClick={() => save({ status, ajuste_manual: ajusteManual, ajuste_motivo: ajusteMotivo })} disabled={saving} className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
                  </Button>
                  {po.status === "BORRADOR" && (
                    <Button data-testid="po-delete" variant="outline" onClick={remove} className="border-red-500/30 text-red-300 hover:bg-red-500/10 rounded-sm"><Trash2 className="w-4 h-4 mr-1" /> Eliminar</Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {tab === "internal" && internal && (
          <div className="space-y-3">
            <div className="text-xs text-amber-200 bg-amber-500/5 border border-amber-500/30 rounded-sm p-3 flex items-center gap-2"><DollarSign className="w-3.5 h-3.5" /> Vista interna · nunca se envía al proveedor.</div>
            <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
              <CardContent className="p-4">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                      <th className="text-left pb-2">Ítem</th>
                      <th className="text-right pb-2">Cant</th>
                      <th className="text-right pb-2">Costo</th>
                      <th className="text-right pb-2">Precio cliente</th>
                      <th className="text-right pb-2">Margen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {internal.lines.map((l) => (
                      <tr key={l.id} className="border-b border-zinc-900">
                        <td className="py-2 text-white">{l.name}</td>
                        <td className="py-2 text-right">{l.cantidad}{l.unit}</td>
                        <td className="py-2 text-right font-mono">{l.costo_total?.toLocaleString?.()}</td>
                        <td className="py-2 text-right font-mono">{l.precio_cliente_total != null ? l.precio_cliente_total.toLocaleString() : "—"}</td>
                        <td className="py-2 text-right font-mono text-emerald-300">{l.margen_total != null ? `${l.margen_total.toLocaleString()} (${l.margen_pct}%)` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-zinc-800 font-semibold">
                      <td className="pt-3">Totales</td>
                      <td></td>
                      <td className="pt-3 text-right font-mono">{internal.totales_internos.costo_total?.toLocaleString?.()}</td>
                      <td className="pt-3 text-right font-mono">{internal.totales_internos.venta_total != null ? internal.totales_internos.venta_total.toLocaleString() : "—"}</td>
                      <td className="pt-3 text-right font-mono text-emerald-300">{internal.totales_internos.margen_total != null ? `${internal.totales_internos.margen_total.toLocaleString()} (${internal.totales_internos.margen_pct_total}%)` : "—"}</td>
                    </tr>
                  </tfoot>
                </table>
              </CardContent>
            </Card>
          </div>
        )}

        {tab === "supplier" && supplierView && (
          <div className="space-y-3">
            <div className="text-xs text-blue-200 bg-blue-500/5 border border-blue-500/30 rounded-sm p-3 flex items-center gap-2"><EyeOff className="w-3.5 h-3.5" /> Así verá el proveedor el pedido. Sin costos internos, sin precio al cliente, sin observaciones internas.</div>
            <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
              <CardContent className="p-4 text-xs space-y-2">
                <div><span className="text-zinc-500">Cliente:</span> <span className="text-white">{supplierView.cliente_empresa}</span></div>
                {supplierView.cliente_contacto && <div><span className="text-zinc-500">Contacto:</span> <span className="text-white">{supplierView.cliente_contacto}</span></div>}
                {supplierView.cliente_email && <div><span className="text-zinc-500">Email:</span> <span className="text-white">{supplierView.cliente_email}</span></div>}
                {supplierView.cliente_whatsapp && <div><span className="text-zinc-500">WhatsApp:</span> <span className="text-white">{supplierView.cliente_whatsapp}</span></div>}
                <div className="mt-3 border-t border-zinc-800 pt-3">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Ítems ({(supplierView.items || []).length})</div>
                  {(supplierView.items || []).map((it) => (
                    <div key={it.id} className="border border-zinc-800 rounded-sm p-2 mb-1">
                      <div className="text-white">{it.name} <span className="text-zinc-500">× {it.cantidad}{it.unit}</span></div>
                      <div className="text-zinc-500 font-mono text-[11px]">Costo acordado: {it.costo_unit?.toLocaleString?.()} {supplierView.moneda} / {it.unit}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 border-t border-zinc-800 pt-3 flex justify-between font-mono">
                  <span className="text-zinc-500">Total contratación</span>
                  <span className="text-white">{supplierView.totals?.total_final?.toLocaleString?.()} {supplierView.moneda}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const MetaBlock = ({ title, rows }) => (
  <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
    <CardContent className="p-4">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">{title}</div>
      <div className="text-xs space-y-1">
        {rows.filter(([, v]) => v != null && v !== "").map(([k, v], i) => (
          <div key={`${k}-${i}`} className="flex justify-between gap-2">
            <span className="text-zinc-400">{k}</span>
            <span className="text-white text-right truncate">{String(v)}</span>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const PriceRow = ({ k, v, moneda, emphasize }) => (
  <div className={`flex justify-between px-2 py-1 rounded-sm ${emphasize ? "bg-[#FF5A00]/10 border border-[#FF5A00]/30" : ""}`}>
    <span className="text-zinc-500">{k}</span>
    <span className={`font-mono ${emphasize ? "text-[#FF5A00]" : "text-white"}`}>{(v || 0).toLocaleString()} {moneda}</span>
  </div>
);
